import { inject, Injectable } from '@angular/core';
import { AuthService as Auth0Service, User } from '@auth0/auth0-angular';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth0 = inject(Auth0Service)

  /**
   * Indicates whether the Auth0 SDK is currently processing authentication-related operations.
   *
   * The `isAuth0Loading$()` method accesses the `isLoading$` observable provided by the `AuthService`.
   * This observable emits a boolean value:
   * - `true`: The Auth0 SDK is still loading or processing an operation (e.g., checking session status, handling redirects).\n
   * - `false`: The Auth0 SDK has completed its operations and is ready.
   *
   * @returns An observable that emits the loading state of the Auth0 SDK.
   */
  public isAuth0Loading$(): Observable<boolean> {
    return this.auth0.isLoading$;
  }

  /**
   * Checks whether the user is authenticated.
   *
   * The `isAuthenticated$()` method accesses the `isAuthenticated$` observable provided by the `AuthService`.
   * This observable emits a boolean value indicating the user's authentication status.
   *
   * @returns An observable that emits `true` if the user is authenticated and `false` otherwise.
   */
  public isAuthenticated$(): Observable<boolean> {
    return this.auth0.isAuthenticated$;
  }

  /**
   * Retrieves an observable of the authenticated user's information.
   *
   * The `authUser$()` method accesses the `user$` observable provided by the `AuthService`.
   * This observable emits the user's profile information as returned by Auth0 after successful authentication.
   *
   * @returns An observable that emits the user's profile data.
   */
  public authUser$(): Observable<User | null | undefined> {
    return this.auth0.user$;
  }

  /**
   * Initiates the authentication process by redirecting the user to the Auth0 `/authorize` endpoint.
   *
   * Within the `LoginButtonComponent` definition, `this.auth.loginWithRedirect()` is a method provided by
   * the `AuthService`. It starts the authentication process by redirecting to the Auth0 `/authorize` endpoint.
   * You can pass a configuration object to customize the login behavior.
   *
   * The `appState.target` property is used to specify a post-login navigation target. For example:
   * - If `appState.target` is set to `/profile`, the Auth0 Angular SDK will redirect users to the "Profile"
   *   page (`/profile`) after they log in and return to the Angular application.
   * - By default, if no `appState.target` is specified, users are redirected to the `/` path after logging in.
   *
   * @param target - An optional string indicating the path to redirect users to after login (e.g., `/profile`).
   */
  public login(target: string): Observable<void> {
    return this.auth0.loginWithRedirect({
      appState: {
        target,
      },
    });
  }

  /**
   * Logs the user out of the Auth0 session and redirects them to the specified URL.
   *
   * The `logout()` method provided by the `AuthService` ends the user's session on Auth0.
   * It takes a configuration object to customize the logout behavior.
   *
   * The `logoutParams.returnTo` property determines the URL where users are redirected
   * after logging out. In this implementation, it redirects users back to the origin
   * of the current document (`doc.location.origin`).
   *
   * @param doc - The `Document` object used to retrieve the application's origin for the redirect URL.
   */
  public logout(doc?: Document): Observable<void> {
    return this.auth0.logout({
      logoutParams: {
        returnTo: doc?.location.origin || '',
      },
    });
  }

}
